import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useVendorStore } from '../stores/vendorStore';
import { useIsUploading } from '../stores/uploadStatusStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { VerificationPanel } from './bank/VerificationPanel';
import { Save, ShieldCheck, UserCheck, Download } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import { STATUS_STYLES } from '../utils/bankConstants';

// Tab components
import ProfileTab from './bank/tabs/ProfileTab';
import CredentialsTab from './bank/tabs/CredentialsTab';
import CardsTab from './bank/tabs/CardsTab';
import UpiTab from './bank/tabs/UpiTab';
import KycTab from './bank/tabs/KycTab';
import SimTab from './bank/tabs/SimTab';
import RechargesTab from './bank/tabs/RechargesTab';
import PricingTab from './bank/tabs/PricingTab';
import AuditTab from './bank/tabs/AuditTab';

const BankDetailDialogEnhanced = ({ open, onOpenChange }) => {
  const user = useAuthStore((s) => s.user);
  const { selectedBank: bank, selectedVendor: vendor, updateBank, addRecharge, deleteRecharge, customCredentialTypes, fetchCredentialTypes, addCredentialType, deleteCredentialType, updateVerification, assignPartner } = useVendorStore();
  const isUploading = useIsUploading();

  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [simInputs, setSimInputs] = useState([{ number: '', network: '' }]);
  const [credentialInputs, setCredentialInputs] = useState([]);
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', freezeReason: '', freezeDate: '', freezedAmount: 0 });
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [partners, setPartners] = useState([]);
  const [assignPartnerId, setAssignPartnerId] = useState('');
  const [showAssignPanel, setShowAssignPanel] = useState(false);

  useEffect(() => { fetchCredentialTypes(); }, [fetchCredentialTypes]);

  useEffect(() => {
    if (user?.role === 'Admin' || user?.role === 'Sub Admin' || user?.role === 'Super Admin') {
      api.getPartners().then(r => setPartners(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const loadBankData = useCallback((bankData) => {
    setEditData({ ...bankData });
    setShowStatusPanel(false);
    setEditingField(null);
    setSimInputs(bankData.simNumbers?.length > 0
      ? bankData.simNumbers.map((num, idx) => ({ number: num, network: bankData.simNetworks?.[idx] || '' }))
      : [{ number: '', network: '' }]);
    setCredentialInputs(bankData.credentials?.length > 0
      ? bankData.credentials.map(c => ({ label: c.label, value: c.value, customLabel: '' }))
      : []);
  }, []);

  useEffect(() => {
    if (bank) {
      loadBankData(bank);
      if (initialLoad) { setActiveTab('profile'); setInitialLoad(false); }
      if (vendor && bank.id && !bank.qrDataUrl && !bank._fullLoaded) {
        api.getBankFull(vendor.id, bank.id).then(res => {
          setEditData(prev => ({ ...prev, ...res.data, _fullLoaded: true }));
        }).catch(() => {});
      }
    }
  }, [bank, vendor, initialLoad, loadBankData]);

  useEffect(() => {
    if (!open) { setInitialLoad(true); }
  }, [open]);

  if (!bank || !vendor) return null;

  const canEdit = user?.canEditBank !== false;
  const updateField = (field, value) => setEditData(prev => ({ ...prev, [field]: value }));

  // Inline edit helpers (shared across tabs via props)
  const startEditing = (field, value) => {
    if (!canEdit) { toast.error("You don't have permission to edit bank details"); return; }
    setEditingField(field);
    setEditingValue(value || '');
  };
  const confirmEdit = (field) => {
    if (field.startsWith('cred-')) {
      const idx = parseInt(field.split('-')[1]);
      const n = [...credentialInputs]; n[idx].value = editingValue; setCredentialInputs(n);
    } else {
      updateField(field, editingValue);
    }
    setEditingField(null); setEditingValue('');
  };
  const cancelEdit = () => { setEditingField(null); setEditingValue(''); };

  const editProps = { canEdit, editingField, editingValue, setEditingValue, startEditing, confirmEdit, cancelEdit };

  // Export helpers
  const handleExport = async (format) => {
    try {
      const res = await api.exportBankFile(vendor.id, bank.id, format);
      const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${bank.bankName}_${bank.companyName || ''}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
  };

  // Save
  const handleSave = async () => {
    editData.simNumbers = simInputs.map(s => s.number).filter(Boolean);
    editData.simNetworks = simInputs.map(s => s.network).filter(Boolean);
    editData.credentials = credentialInputs
      .map(c => ({ label: c.label === 'Custom' ? c.customLabel : c.label, value: c.value }))
      .filter(c => c.label && c.value);

    const changes = {};
    Object.keys(editData).forEach(key => {
      if (key === 'recharges' || key === 'pricingHistory') return;
      if (JSON.stringify(editData[key]) !== JSON.stringify(bank[key])) changes[key] = editData[key];
    });

    if (Object.keys(changes).length > 0) {
      try {
        await updateBank(vendor.id, bank.id, changes);
        toast.success('Bank updated successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update bank');
      }
    }
  };

  const handleStatusSubmit = async () => {
    const updates = { status: statusForm.status };
    if (statusForm.status.startsWith('Freezed')) {
      if (!statusForm.freezeReason || !statusForm.freezeDate) { toast.error('Freeze reason and date are required'); return; }
      updates.freezeReason = statusForm.freezeReason;
      updates.freezeDate = statusForm.freezeDate;
      updates.freezedAmount = Number(statusForm.freezedAmount) || 0;
    } else if (bank.status?.startsWith('Freezed')) {
      updates.freezedAmount = 0; updates.freezeReason = ''; updates.freezeDate = '';
    }
    try {
      await updateBank(vendor.id, bank.id, updates);
      toast.success('Status updated');
      setShowStatusPanel(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleVerificationUpdate = async (checkName, data) => {
    try {
      await updateVerification(vendor.id, bank.id, checkName, data);
      toast.success(`${checkName} verification updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification');
      // Re-throw so the caller (e.g. UploadInput) knows the upload didn't persist
      // and skips the "✓ uploaded" success indicator.
      throw error;
    }
  };

  const handleScreenshotUpload = async (checkName, file, notes) => {
    const isNetOrMissing = (err) => !err?.response || [404, 405, 502].includes(err.response.status);

    // 1) Try multipart endpoint (large-file safe).
    try {
      const res = await api.uploadVerificationScreenshot(vendor.id, bank.id, checkName, file, notes);
      useVendorStore.setState({ selectedBank: res.data });
      await useVendorStore.getState().fetchVendors();
      toast.success(`${checkName} screenshot uploaded`);
      return;
    } catch (err) {
      if (!isNetOrMissing(err)) {
        toast.error(err?.response?.data?.detail || 'Failed to upload screenshot');
        throw err;
      }
    }

    // 2) Fallback: compress client-side, send as base64 JSON via existing endpoint.
    try {
      const { prepareUploadDataUrl } = await import('../utils/imageUpload');
      const { dataUrl } = await prepareUploadDataUrl(file, { allowPdf: false });
      const existingNotes = bank.verification?.[checkName]?.notes || '';
      const existingVerified = bank.verification?.[checkName]?.verified || false;
      await updateVerification(vendor.id, bank.id, checkName, {
        verified: existingVerified,
        screenshotUrl: dataUrl,
        notes: notes || existingNotes,
      });
      toast.info(`${checkName} screenshot uploaded (fallback)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to upload screenshot');
      throw err;
    }
  };

  const handleAssignPartner = async () => {
    if (!assignPartnerId) { toast.error('Please select a branch'); return; }
    try {
      const wasAssigned = !!bank.assignedPartnerId;
      await assignPartner(vendor.id, bank.id, assignPartnerId);
      toast.success(wasAssigned ? 'Bank reassigned to new branch' : 'Branch assigned, bank is now Active');
      setShowAssignPanel(false);
      setAssignPartnerId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign branch');
    }
  };

  const getStatusBadge = (status) => (
    <Badge className={STATUS_STYLES[status] || ''}>{status}</Badge>
  );

  const recharges = bank.recharges || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>{vendor.name} - {bank.bankName}</DialogTitle>
              {bank.companyName && (
                <div className="mt-1 text-xs text-slate-500" data-testid="bank-detail-company-name">
                  Company: <span className="font-medium text-slate-700">{bank.companyName}</span>
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-[10px] uppercase tracking-wide">Primary</span>
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-slate-600">Status:</span>
                {getStatusBadge(bank.status)}
                {bank.bankStartDate && <span className="text-xs text-slate-500">Started: {bank.bankStartDate}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('excel')} data-testid="export-bank-excel"><Download className="w-3 h-3" />Excel</Button>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleExport('pdf')} data-testid="export-bank-pdf"><Download className="w-3 h-3" />PDF</Button>
              {canEdit && (user?.role === 'Admin' || user?.role === 'Sub Admin' || user?.role === 'Super Admin') && (
                <Button onClick={() => setShowAssignPanel(true)} variant="outline" size="sm" data-testid="assign-partner-btn" className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <UserCheck className="w-4 h-4" /> {bank.assignedPartnerId ? 'Reassign Branch' : 'Assign Branch'}
                </Button>
              )}
              {canEdit && <Button onClick={() => { setStatusForm({ status: bank.status || 'Active', freezeReason: bank.freezeReason || '', freezeDate: bank.freezeDate || '', freezedAmount: bank.freezedAmount || 0 }); setShowStatusPanel(true); }} variant="outline" size="sm" data-testid="change-status-btn">Change Status</Button>}
              {canEdit && <Button onClick={handleSave} size="sm" disabled={isUploading} title={isUploading ? 'Wait for uploads to finish' : ''} className="gap-2" data-testid="save-changes-btn"><Save className="w-4 h-4" />{isUploading ? 'Uploading…' : 'Save Changes'}</Button>}
            </div>
          </div>

          {showStatusPanel && (
            <div className="mt-3 p-4 bg-slate-50 border rounded-lg space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-56">
                  <Label className="text-xs mb-1 block">New Status</Label>
                  <Select value={statusForm.status} onValueChange={(val) => setStatusForm(p => ({ ...p, status: val }))}>
                    <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['New Bank', 'In Process', 'Unassigned', 'Active', 'Inactive', 'Freezed - Transaction', 'Freezed - CBI', 'Closed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {statusForm.status.startsWith('Freezed') && (
                  <>
                    <div className="w-48"><Label className="text-xs mb-1 block">Freeze Reason</Label><Input data-testid="freeze-reason" value={statusForm.freezeReason} onChange={(e) => setStatusForm(p => ({ ...p, freezeReason: e.target.value }))} placeholder="Reason" /></div>
                    <div className="w-40"><Label className="text-xs mb-1 block">Freeze Date</Label><Input data-testid="freeze-date" type="date" value={statusForm.freezeDate} onChange={(e) => setStatusForm(p => ({ ...p, freezeDate: e.target.value }))} /></div>
                    <div className="w-36"><Label className="text-xs mb-1 block">Freezed Amount</Label><Input data-testid="freeze-amount" type="number" value={statusForm.freezedAmount} onChange={(e) => setStatusForm(p => ({ ...p, freezedAmount: e.target.value }))} /></div>
                  </>
                )}
                <div className="flex gap-2 self-end">
                  <Button data-testid="status-submit-btn" size="sm" onClick={handleStatusSubmit}>Apply</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowStatusPanel(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {showAssignPanel && (
            <div className="mt-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3">
              <h4 className="text-sm font-semibold text-emerald-800">{bank.assignedPartnerId ? 'Reassign Branch' : 'Assign to Branch'}</h4>
              {bank.assignedPartnerName && (
                <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">Currently assigned to: <strong>{bank.assignedPartnerName}</strong>. Reassigning will transfer this bank to the new branch and log the change.</p>
              )}
              {!bank.assignedPartnerId && <p className="text-xs text-emerald-700">Assigning a branch will move this bank to "Active" status.</p>}
              <div className="flex items-end gap-3">
                <div className="w-64">
                  <Label className="text-xs mb-1 block">Select Branch</Label>
                  <Select value={assignPartnerId} onValueChange={setAssignPartnerId}>
                    <SelectTrigger data-testid="assign-partner-select"><SelectValue placeholder="Choose branch..." /></SelectTrigger>
                    <SelectContent>
                      {partners.filter(p => p.active !== false && p.id !== bank.assignedPartnerId).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} {p.branch ? `(${p.branch})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" onClick={handleAssignPartner} data-testid="confirm-assign-partner" className="bg-emerald-600 hover:bg-emerald-700">{bank.assignedPartnerId ? 'Reassign' : 'Assign'}</Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowAssignPanel(false); setAssignPartnerId(''); }}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${
            ((user?.role === 'Admin' || user?.role === 'Super Admin') ? 10 :
            user?.role === 'Partner' ? 8 : 9) +
            (((user?.role === 'Admin' || user?.role === 'Sub Admin' || user?.role === 'Super Admin') && bank.assignedPartnerId) ? 1 : 0)
          }, minmax(0, 1fr))` }}>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            {user?.role !== 'Partner' && (bank.status === 'New Bank' || bank.status === 'In Process' || bank.status === 'Unassigned' || bank.status === 'Active') && (
              <TabsTrigger value="verification" data-testid="verification-tab">
                <ShieldCheck className="w-3 h-3 mr-1" />Verify
              </TabsTrigger>
            )}
            <TabsTrigger value="credentials" data-testid="credentials-tab">Credentials</TabsTrigger>
            <TabsTrigger value="cards">Cards</TabsTrigger>
            <TabsTrigger value="upi">UPI & QR</TabsTrigger>
            <TabsTrigger value="kyc">KYC</TabsTrigger>
            <TabsTrigger value="sim">SIM</TabsTrigger>
            <TabsTrigger value="recharges" data-testid="recharges-tab">
              Recharges {recharges.length > 0 && <Badge className="ml-1 bg-indigo-100 text-indigo-800 text-xs px-1.5">{recharges.length}</Badge>}
            </TabsTrigger>
            {(user?.role === 'Admin' || user?.role === 'Super Admin') && <TabsTrigger value="pricing">Pricing</TabsTrigger>}
            {(user?.role === 'Admin' || user?.role === 'Sub Admin' || user?.role === 'Super Admin') && bank.assignedPartnerId && (
              <TabsTrigger value="partner-info"><UserCheck className="w-3 h-3 mr-1" />Branch</TabsTrigger>
            )}
            <TabsTrigger value="audit">Audit</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            <TabsContent value="profile" className="mt-0">
              <ProfileTab bank={bank} editData={editData} updateField={updateField} {...editProps} />
            </TabsContent>

            <TabsContent value="verification" className="mt-0 space-y-4">
              <VerificationPanel bank={bank} vendor={vendor} vendorId={vendor.id} canEdit={canEdit} onUpdate={handleVerificationUpdate} onScreenshotUpload={handleScreenshotUpload} />
            </TabsContent>

            <TabsContent value="credentials" className="mt-0">
              <CredentialsTab
                bank={bank} canEdit={canEdit} user={user}
                credentialInputs={credentialInputs} setCredentialInputs={setCredentialInputs}
                customCredentialTypes={customCredentialTypes}
                addCredentialType={addCredentialType} deleteCredentialType={deleteCredentialType}
                {...editProps}
              />
            </TabsContent>

            <TabsContent value="cards" className="mt-0">
              <CardsTab editData={editData} updateField={updateField} {...editProps} />
            </TabsContent>

            <TabsContent value="upi" className="mt-0">
              <UpiTab editData={editData} updateField={updateField} {...editProps} />
            </TabsContent>

            <TabsContent value="kyc" className="mt-0">
              <KycTab editData={editData} updateField={updateField} {...editProps} />
            </TabsContent>

            <TabsContent value="sim" className="mt-0">
              <SimTab editData={editData} updateField={updateField} simInputs={simInputs} setSimInputs={setSimInputs} {...editProps} />
            </TabsContent>

            <TabsContent value="recharges" className="mt-0">
              <RechargesTab bank={bank} vendor={vendor} canEdit={canEdit} addRecharge={addRecharge} deleteRecharge={deleteRecharge} />
            </TabsContent>

            <TabsContent value="pricing" className="mt-0">
              <PricingTab editData={editData} updateField={updateField} user={user} bank={bank} />
            </TabsContent>

            <TabsContent value="partner-info" className="mt-0 space-y-4">
              {bank.assignedPartnerId && (
                <Card className="p-4 space-y-3 border-emerald-200">
                  <h3 className="font-semibold text-sm text-emerald-800 flex items-center gap-2"><UserCheck className="w-4 h-4" /> Assigned Branch</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-slate-500">Partner Name:</span><span className="ml-2 font-medium">{bank.assignedPartnerName || '-'}</span></div>
                    <div><span className="text-slate-500">Partner ID:</span><span className="ml-2 font-mono text-xs">{bank.assignedPartnerId}</span></div>
                  </div>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="audit" className="mt-0">
              <AuditTab bank={bank} />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default BankDetailDialogEnhanced;
